import { X, Calendar } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Input,
  Label,
} from "@b2b/ui-components";

interface UserAdvancedFiltersProps {
  filters: {
    role?: string;
    roles?: string[];
    companyId?: string;
    country?: string;
    countries?: string[];
    isActive?: boolean;
    approvalStatus?: string;
    startDate?: string;
    endDate?: string;
  };
  onFiltersChange: (filters: any) => void;
  onReset: () => void;
  companies?: Array<{ id: string; name: string }>;
  countries?: string[];
  isLoading?: boolean;
}

export function UserAdvancedFilters({
  filters,
  onFiltersChange,
  onReset,
  companies = [],
  countries = [],
  isLoading = false,
}: UserAdvancedFiltersProps) {
  const updateFilter = (key: string, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const roleOptions = [
    { value: "all", label: "All Roles" },
    { value: "BUYER", label: "Buyer" },
    { value: "SELLER", label: "Seller" },
    { value: "BUYER_SELLER", label: "Buyer & Seller" },
    { value: "COMPANY_ADMIN", label: "Company Admin" },
    { value: "EMPLOYEE", label: "Employee" },
    { value: "SUPER_ADMIN", label: "Super Admin" },
  ];

  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const approvalStatusOptions = [
    { value: "all", label: "All Approval Status" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  return (
    <Card className="shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Advanced Filters</CardTitle>
          <Button variant="ghost" size="sm" onClick={onReset}>
            <X className="h-4 w-4 mr-2" />
            Reset
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Role Filter */}
          <div className="space-y-2">
            <Label>Role</Label>
            <Select
              value={filters.role || "all"}
              onValueChange={(value) =>
                updateFilter("role", value === "all" ? undefined : value)
              }
              disabled={isLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {roleOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={
                filters.isActive === undefined
                  ? "all"
                  : filters.isActive
                    ? "active"
                    : "inactive"
              }
              onValueChange={(value) => {
                if (value === "all") {
                  updateFilter("isActive", undefined);
                } else {
                  updateFilter("isActive", value === "active");
                }
              }}
              disabled={isLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Approval Status Filter */}
          <div className="space-y-2">
            <Label>Approval Status</Label>
            <Select
              value={filters.approvalStatus || "all"}
              onValueChange={(value) =>
                updateFilter(
                  "approvalStatus",
                  value === "all" ? undefined : value
                )
              }
              disabled={isLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select approval status" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {approvalStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Company Filter */}
          {companies.length > 0 && (
            <div className="space-y-2">
              <Label>Company</Label>
              <Select
                value={filters.companyId || "all"}
                onValueChange={(value) =>
                  updateFilter("companyId", value === "all" ? undefined : value)
                }
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Companies</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Country Filter */}
          {countries.length > 0 && (
            <div className="space-y-2">
              <Label>Country</Label>
              <Select
                value={filters.country || "all"}
                onValueChange={(value) =>
                  updateFilter("country", value === "all" ? undefined : value)
                }
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Countries</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>
                      {country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date From */}
          <div className="space-y-2">
            <Label>Date From</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="date"
                value={filters.startDate || ""}
                onChange={(e) =>
                  updateFilter("startDate", e.target.value || undefined)
                }
                className="pl-9"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Date To */}
          <div className="space-y-2">
            <Label>Date To</Label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="date"
                value={filters.endDate || ""}
                onChange={(e) =>
                  updateFilter("endDate", e.target.value || undefined)
                }
                className="pl-9"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
